<template>
  <mf-card-container :hide-card-shadow="isMariaCampaign" title="Redirecionamento">
    <mf-tabs-button v-model="computedValue" :disabled="disabled" class="mt-3" :buttons="tabs" />

    <div v-if="tab === 'external'">
      <p class="hint-text external mt-2">
        Nessa modalidade o usuário é redirecionado para uma página web externa. Só poderemos levar seus consumidores para endereços seguros, por isso o único
        tipo de link permitido é o que começa com <b>https://</b>. Certifique-se de inserir um link seguro aqui:
      </p>

      <v-text-field
        v-model="external"
        :error="externalError"
        :error-messages="externalError ? 'Você precisa incluir um link válido' : ''"
        dense
        placeholder="https://"
        label="Link"
        outlined
        :disabled="disabled"
      />
    </div>

    <div v-if="tab === 'internal'">
      <p class="hint-text mt-2 mb-0">Selecione a tela do aplicativo para onde o banner deve redirecionar.</p>
      <v-radio-group v-model="internal" dense hide-details :disabled="disabled" class="mt-0">
        <v-row>
          <v-col v-for="(item, index) in links" :key="index" cols="3">
            <mf-tooltip top max-width="250px" variant="secondary" :disabled="item && item.link !== 'InternalEcommerce'">
              <template #activator="{ on }">
                <v-radio v-if="item" :label="item.title" color="primary" :value="item.link" v-on="on" />
              </template>
              <span>O cliente será levado à página inicial do E-commerce</span>
            </mf-tooltip>
          </v-col>
        </v-row>
      </v-radio-group>

      <div v-if="internal === 'FlyersScreen'">
        <p class="hint-text mt-2 mb-2">Caso queira redirecionar o consumidor para um encarte específico, selecione abaixo:</p>
        <v-select v-model="localSelectedFlyer" item-value="id" outlined :items="flyersOptions" label="Encarte" item-text="title" style="width: 300px" dense />
      </div>
    </div>

    <div v-if="tab === 'offer'" class="mt-5">
      <div v-if="isMariaCampaign" class="offer-message-container">
        <p class="mb-0">
          A campanha/oferta que foi selecionada por você na primeira etapa será exibida para os seus usuários através do push, no próximo passo (Confirmação da
          campanha) vai ser possível você rever todos os produtos que fazem parte da seleção.
        </p>
      </div>

      <div v-else-if="!isMariaCampaign && !offerScheduleId && products.length <= 0" class="offer-message-container">
        <h2 class="mb-5">Nenhuma oferta ou produto foi selecionado</h2>
        <p class="mb-0">
          Divulgue as ofertas dessa campanha com seus clientes, selecione no card de Gestão de ofertas ou no card de Produtos, nós mostraremos essas ofertas
          para o cliente quando ele clicar na push.
        </p>
      </div>

      <div v-else class="offer-message-container">
        <p class="mb-0">
          Seus usuários serão redirecionados para uma tela com as ofertas ou produtos selecionados. Para que produtos sejam visualizados no aplicativo eles
          precisam estar em oferta.
        </p>
      </div>
    </div>
  </mf-card-container>
</template>

<script>
import { QUERY_GET_APP_LINKS } from '@/modules/mobile/banners/graphql'
import { QUERY_FLYERS } from '@/modules/mobile/encartes/graphql'

export default {
  name: 'Redirection',

  props: {
    tab: {
      type: String,
      default: '',
    },
    isMariaCampaign: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    validations: {
      type: Object,
      default: () => {},
    },
    offerScheduleId: {
      type: String,
      default: '',
    },
    redirectExternal: {
      type: String,
      default: '',
    },
    redirectInternal: {
      type: String,
      default: '',
    },
    products: {
      type: Array,
      default: () => [],
    },
    selectedFlyer: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    search: '',
    offersTypeTabHighLevel: 0,
    offersTypeTabLowLevel: 0,
    flyersOptions: [],
  }),
  computed: {
    externalError() {
      return this.validations?.redirectExternal?.$error
    },
    localSelectedFlyer: {
      get() {
        return this.selectedFlyer
      },
      set(value) {
        this.$emit('update:selectedFlyer', value)
      },
    },
    external: {
      get() {
        return this.redirectExternal
      },
      set(value) {
        this.$emit('update:redirectExternal', value)
      },
    },
    internal: {
      get() {
        return this.redirectInternal
      },
      set(value) {
        this.$emit('update:redirectInternal', value)
      },
    },
    computedValue: {
      get() {
        return this.tab
      },
      set(value) {
        this.$emit('update:tab', value)
      },
    },
    tabs() {
      return [
        {
          id: 0,
          label: 'Link externo',
          value: 'external',
          active: true,
        },
        {
          id: 1,
          label: 'Link interno',
          value: 'internal',
          active: true,
        },
        {
          id: 2,
          label: 'Vincular oferta',
          value: 'offer',
          active: true,
        },
      ]
    },
    links() {
      const home = {
        title: 'Página inicial',
        link: 'home',
      }

      return [home].concat(this.appPagesLinks)
    },
  },
  apollo: {
    appPagesLinks: {
      query: QUERY_GET_APP_LINKS,
    },
    flyers: {
      query: QUERY_FLYERS,
      fetchPolicy: 'no-cache',
      notifyOnNetworkStatusChange: true,
      variables() {
        const variables = {
          status: ['ACTIVE', 'SCHEDULED'],
        }
        return variables
      },
      update(data) {
        this.flyersOptions = [
          ...data.flyers.flyers.map((flyer) => ({
            title: flyer.title,
            id: flyer._id,
            initialDate: flyer.initial_date,
            finalDate: flyer.final_date,
          })),
        ]
      },
    },
  },
  mounted() {
    if (this.offerScheduleId) this.computedValue = 'offer'
  },
}
</script>

<style lang="scss" scoped>
.hint-text {
  font-size: 14px;
  line-height: 20px;
  max-width: 700px;

  b {
    color: $marca-1;
    font-weight: 700;
  }
}

.offer-message-container {
  padding: 24px;
  background: $cinza-l90;
  h2 {
    font-weight: 700;
    color: $cinza-d20;
    font-size: 18px;
    text-align: center;
  }

  p {
    font-weight: 400;
    color: $cinza-0;
    font-size: 14px;
    text-align: center;
    margin: 0 auto;
    max-width: 730px;
  }
}

.sticky-headers {
  background: $branco !important;
}
</style>
